import {
  collectProgramContentSlideDraftScopeIds,
  discardPendingProgramContentSlideDraftsForScopes,
  flushPendingProgramContentSlideDrafts,
  hasPendingProgramContentSlideDraftsForScope,
} from '../services/programContentSlideService';
import {
  collectPendingLearningWorksheetScopeIds,
  discardPendingLearningWorksheetDraftsForScopes,
  flushPendingLearningWorksheetDrafts,
  hasPendingLearningWorksheetDraftsForScope,
} from './learningContentWorksheetPendingStorage';
import {
  discardAllDirtyProgramWorkspaceScopes,
  getProgramWorkspaceScopeEntries,
  isAnyProgramWorkspaceScopeDirty,
} from './programEditWorkspace';
import {
  emitProgramProgressSaved,
  type ProgramProgressUiRefreshMode,
} from './programProgressSavedEvents';
import { mergeFlushedLearningOwnerRowsIntoCaches } from './programAttachmentRowCacheMerge';
import { saveProgramWorkspaceAll } from './programUnifiedSave';

export type ProgramGradeTermSaveTarget = {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
};

export function scopeIdMatchesProgramGradeTerm(
  scopeId: string,
  target: ProgramGradeTermSaveTarget
): boolean {
  const parts = scopeId.split('|');
  if (parts.length < 5) return false;
  const termNumber = parts[4] === '1' ? 1 : parts[4] === '2' ? 2 : null;
  if (!termNumber) return false;
  const academicYear = Number(parts[3]);
  if (!Number.isFinite(academicYear)) return false;
  return (
    parts[1].trim() === target.workspaceId.trim() &&
    parts[2].trim() === target.gradeId.trim() &&
    academicYear === target.academicYear &&
    termNumber === target.termNumber
  );
}

/** 현재 학년·학기 프로그램 워크스페이스 + 슬라이드·활동지 pending flush 대상 scopeId */
export function collectProgramGradeTermScopeIds(target: ProgramGradeTermSaveTarget): string[] {
  const workspaceId = target.workspaceId.trim();
  const gradeId = target.gradeId.trim();
  if (!workspaceId || !gradeId) return [];

  const idSet = new Set<string>();
  getProgramWorkspaceScopeEntries().forEach((entry) => {
    if (
      entry.workspaceId === workspaceId &&
      entry.gradeId === gradeId &&
      entry.academicYear === target.academicYear &&
      entry.termNumber === target.termNumber
    ) {
      idSet.add(entry.scopeId);
    }
  });

  collectProgramContentSlideDraftScopeIds().forEach((scopeId) => {
    if (scopeIdMatchesProgramGradeTerm(scopeId, target)) idSet.add(scopeId);
  });

  collectPendingLearningWorksheetScopeIds().forEach((scopeId) => {
    if (scopeIdMatchesProgramGradeTerm(scopeId, target)) idSet.add(scopeId);
  });

  return Array.from(idSet);
}

/** 연간진도표·프로그램 페이지 공통: 해당 학년·학기에 저장할 프로그램 변경이 있는지 */
export function isProgramGradeTermSavePending(target: ProgramGradeTermSaveTarget): boolean {
  const scopeIds = collectProgramGradeTermScopeIds(target);
  if (!scopeIds.length) return false;
  if (isAnyProgramWorkspaceScopeDirty(scopeIds)) return true;
  if (scopeIds.some((id) => hasPendingProgramContentSlideDraftsForScope(id))) return true;
  if (scopeIds.some((id) => hasPendingLearningWorksheetDraftsForScope(id))) return true;
  return false;
}

/**
 * 「저장하지 않고 이동」— leave dirty를 만드는 LS 초안·워크스페이스를 모두 폐기한다.
 * (leave 판정이 전역 dirty/초안을 포함하므로 현재 페이지 scope만 지우면 모달이 다시 뜬다.)
 */
export function discardAllProgramFamilyLeaveDrafts(): void {
  discardAllDirtyProgramWorkspaceScopes();
  discardPendingProgramContentSlideDraftsForScopes();
  discardPendingLearningWorksheetDraftsForScopes();
}

/**
 * 프로그램 통합 저장 버튼 활성 여부(연간진도표·편제/고정/프로젝트 페이지 공통).
 * 표 셀 직접 편집(`hasUnsavedTableChanges`)은 프로그램 표가 열려 있을 때만 반영한다.
 */
export function computeProgramSavePending(input: {
  target: ProgramGradeTermSaveTarget;
  hasUnsavedTableChanges?: boolean;
  hasTableSaveAction?: boolean;
}): boolean {
  if (input.hasUnsavedTableChanges && input.hasTableSaveAction) return true;
  const { workspaceId, gradeId } = input.target;
  if (!workspaceId.trim() || !gradeId.trim()) return false;
  return isProgramGradeTermSavePending(input.target);
}

/**
 * 프로그램 페이지「통합 저장」과 동일: 워크스페이스 더티 + 슬라이드·활동지 pending을 DB에 반영.
 * 연간진도표 본문 데이터는 저장하지 않는다.
 * @returns uiRefreshMode
 * - cache: 칸/단일행 첨부만 — upsert·행 SELECT로 캐시 반영, 전량 refetch 생략
 * - full: 표 구조 변경(replace)·sync·캐시 병합 실패 등
 */
export async function saveProgramGradeTermWorkspaceAndAttachments(
  target: ProgramGradeTermSaveTarget,
  options?: { excludeWorkspaceScopeId?: string }
): Promise<{ uiRefreshMode: ProgramProgressUiRefreshMode }> {
  const workspaceId = target.workspaceId.trim();
  const gradeId = target.gradeId.trim();
  if (!workspaceId || !gradeId) return { uiRefreshMode: 'full' };

  const scopeIds = collectProgramGradeTermScopeIds(target);
  const exclude = (options?.excludeWorkspaceScopeId ?? '').trim();
  let attachmentNeedsFull = false;
  const attachmentOwners: Array<{ kind: 'program_cell' | 'fixed_row' | 'scoped_row'; id: string }> = [];
  if (scopeIds.length) {
    /**
     * worksheet_ref_id FK 제약:
     * pending 활동지 문서를 먼저 upsert해 ref 실체를 만든 뒤 워크스페이스 행을 저장한다.
     */
    const worksheetFlush = await flushPendingLearningWorksheetDrafts(scopeIds);
    const slideFlush = await flushPendingProgramContentSlideDrafts(scopeIds);
    attachmentOwners.push(...worksheetFlush.owners, ...slideFlush.owners);
    attachmentNeedsFull = worksheetFlush.needsFullUiRefresh || slideFlush.needsFullUiRefresh;
    if (!attachmentNeedsFull && attachmentOwners.length) {
      const merged = await mergeFlushedLearningOwnerRowsIntoCaches({
        workspaceId,
        academicYear: target.academicYear,
        termNumber: target.termNumber,
        gradeId,
        owners: attachmentOwners,
      });
      if (!merged) attachmentNeedsFull = true;
    }
  }
  const workspaceResult = await saveProgramWorkspaceAll(exclude || undefined);
  const uiRefreshMode: ProgramProgressUiRefreshMode =
    attachmentNeedsFull || workspaceResult.uiRefreshMode === 'full' ? 'full' : 'cache';
  emitProgramProgressSaved({
    workspaceId,
    academicYear: target.academicYear,
    termNumber: target.termNumber,
    gradeId,
    uiRefreshMode,
  });
  return { uiRefreshMode };
}
